import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { X, ChevronDown, ChevronUp, ArrowRight, MapPin, CircleAlert } from 'lucide-react';
import type { Insight } from '../utils/itineraryGaps';
import { L1_BY_KEY } from '../constants/taxonomy';

// Moved here from Home.tsx (originally rendered inline on the Trips tab, in
// two places — the COUNTDOWN branch's "Itinerary Gaps" card and
// TravellingInsight's copy of the same) per explicit request: relocated to
// the Itinerary tab as a compact CTA banner + this portal modal, instead of
// always-visible cards on Home. See TripOverview (Itinerary.tsx) for the
// CTA and the computeItineraryGaps() call site — the detection logic itself
// lives in utils/itineraryGaps.ts, this file is the rendering only.

function InsightAccordion({ insight }: { insight: Insight; key?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const displayedItems = showAll ? insight.items : insight.items.slice(0, 3);
  const hasMore = insight.items.length > 3;
  const Icon = insight.type === 'DESTINATION' ? MapPin : L1_BY_KEY[insight.type.toLowerCase()].icon;

  return (
    <div className="border-b border-app-border/10 last:border-0 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-2 text-left hover:bg-black/[0.02] active:bg-black/[0.04] transition-colors -mx-2 px-2 rounded-xl"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-app-faint rounded-2xl flex items-center justify-center shrink-0 opacity-70">
            <Icon className="w-5 h-5 text-gold" strokeWidth={1.7} aria-hidden="true" />
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <div className="text-app-text text-sm font-bold leading-snug">{{ DESTINATION: 'Destinations', ACCOMMODATION: 'Accommodation', TRANSPORT: 'Transport' }[insight.type] ?? insight.type}{insight.group === 'details' ? ' times' : ''}</div>
            <div className="text-app-sub text-xs font-medium mt-0.5 leading-relaxed pr-4">{insight.message}</div>
          </div>
        </div>
        <div className="text-app-sub/50 px-2 shrink-0">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {expanded && (
        <div className="pl-[3.25rem] pr-2 pb-3 pt-1 space-y-2 animate-fade-in">
          {displayedItems.map((item, i) => (
            <button
              key={item.id || i}
              onClick={item.onClick}
              className="w-full text-left flex items-center justify-between p-2 rounded-lg hover:bg-black/[0.03] transition-colors group"
            >
              <div>
                <div className="text-xs font-medium text-app-text">{item.label}</div>
                {item.sublabel && <div className="text-sm font-medium text-app-sub mt-0.5">{item.sublabel}</div>}
              </div>
              <div className="transition-opacity flex items-center justify-center w-6 h-6 rounded-full bg-black/5 text-app-text text-xs">
                <ArrowRight className="w-3 h-3" />
              </div>
            </button>
          ))}
          {hasMore && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="text-[11px] font-black uppercase tracking-widest text-gold hover:text-gold/80 transition-colors pt-1 px-2 mb-1"
            >
              See more ({insight.items.length - 3})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Portal modal, backdrop-click-to-close — same pattern as every other
// item-level modal in this app (see talisman-portal-modal-migration memory).
export function ItineraryGapsModal({ insights, onClose }: { insights: Insight[]; onClose: () => void }) {
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={onClose}
      className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-app-bg/80 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-app-bg border border-app-border rounded-3xl p-8 max-w-xl w-full max-h-[85vh] overflow-y-auto overflow-x-hidden shadow-2xl"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="font-serif text-2xl font-bold text-app-text">Itinerary review</div>
          <button aria-label="Close" onClick={onClose} className="p-2 text-app-sub/40 hover:text-gold transition-colors"><X className="w-6 h-6" /></button>
        </div>
        {/* Gaps: missing outright, then booked but missing the times the
            Schedule needs. Conflicts: saved records that clash for the same
            traveller — reviewable, never removed automatically. */}
        {([['gaps', 'Gaps'], ['conflicts', 'Conflicts']] as const).map(([group, title]) => {
          const list = group === 'gaps'
            ? [...insights.filter(i => i.group === 'essential'), ...insights.filter(i => i.group === 'details')]
            : insights.filter(i => i.group === 'review');
          if (!list.length) return null;
          return (
            <div key={group} className="mb-4 last:mb-0">
              <h3 className={`ds-label flex items-center gap-2 mb-1 ${group === 'conflicts' ? 'text-rose-700' : 'text-app-sub/70'}`}>
                {group === 'conflicts' && <CircleAlert size={16} aria-hidden="true" />}{title}
              </h3>
              {list.map(insight => <InsightAccordion key={insight.id} insight={insight} />)}
            </div>
          );
        })}
      </motion.div>
    </motion.div>,
    document.getElementById('import-modal-root') ?? document.body
  );
}
